import * as THREE from 'three';
import {
    normalizeRevolveAxisDirection,
    projectRevolveDirectionToAxisPlane,
} from '../../../feature/interaction/revolve/RevolveAngleDragMath';
import { RevolveAngleGizmoPartKind } from './RevolveAngleGizmoPartKind';
import { RevolveAnglePartGroup } from './RevolveAnglePartGroup';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/mesh/feature/revolve 层；
 * 2. 负责封装旋转特征的角度拖拽手柄整体对象；
 * 3. 该对象仅处理显示与拾取部件组织，不处理鼠标事件。
 *
 * 文件职责：
 * 1. 管理 Direction1/Direction2 两个拖拽部件；
 * 2. 根据轴、基准方向和角度刷新姿态；
 * 3. 显式释放内部 Three.js 资源。
 */

/**
 * 旋转角度手柄对象。
 */
export class RevolveAngleGizmoObject extends THREE.Group {
    /** 方向 1 部件。 */
    private readonly direction1Part: RevolveAnglePartGroup;
    /** 方向 2 部件。 */
    private readonly direction2Part: RevolveAnglePartGroup;

    /**
     * 构造旋转角度手柄。
     */
    public constructor() {
        super();
        this.name = '__revolve_angle_gizmo__';
        this.direction1Part = new RevolveAnglePartGroup({
            partKind: RevolveAngleGizmoPartKind.Direction1Head,
            color: 0x18a058,
        });
        this.direction2Part = new RevolveAnglePartGroup({
            partKind: RevolveAngleGizmoPartKind.Direction2Head,
            color: 0xd9901a,
        });
        this.add(this.direction1Part);
        this.add(this.direction2Part);
    }

    /**
     * 从命中对象解析手柄部件类型。
     *
     * @param object 命中对象。
     * @returns 部件类型；未命中时返回 unknown。
     */
    public resolvePartKindFromObject(object: THREE.Object3D | null): RevolveAngleGizmoPartKind | 'unknown' {
        return RevolveAnglePartGroup.resolveDraggableFromObject(object)?.partKind ?? 'unknown';
    }

    /**
     * 定向手柄对象。
     *
     * @param input 姿态输入。
     */
    public orient(input: {
        center: THREE.Vector3;
        axisDirection: THREE.Vector3;
        baseDirection: THREE.Vector3;
    }): void {
        const zAxis = normalizeRevolveAxisDirection(input.axisDirection);
        const xAxis = projectRevolveDirectionToAxisPlane(input.baseDirection, zAxis);
        const yAxis = zAxis.clone().cross(xAxis).normalize();
        const rotation = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
        this.position.copy(input.center);
        this.quaternion.setFromRotationMatrix(rotation);
    }

    /**
     * 更新手柄显示。
     *
     * @param input 显示输入。
     */
    public updateVisual(input: {
        radius: number;
        headSize: number;
        direction1Angle: number;
        direction2Enabled: boolean;
        direction2Angle: number;
    }): void {
        const radius = Math.max(1e-4, input.radius);
        this.direction1Part.updateVisual({
            radius,
            headSize: input.headSize,
            angle: Math.min(Math.PI * 2, Math.max(1e-5, input.direction1Angle)),
            visible: true,
            directionSign: 1,
        });
        this.direction2Part.updateVisual({
            radius: Math.max(1e-4, radius * 0.82),
            headSize: input.headSize,
            angle: input.direction2Enabled
                ? Math.min(Math.PI * 2, Math.max(0, input.direction2Angle))
                : 0,
            visible: input.direction2Enabled,
            directionSign: -1,
        });
    }

    /**
     * 释放手柄资源。
     */
    public dispose(): void {
        this.direction1Part.dispose();
        this.direction2Part.dispose();
    }
}
